import React, { useState } from 'react';
import { X, Bell, Users, Send, Search, Plus, AlertTriangle, Info, CheckCircle } from 'lucide-react';

interface SendNotificationModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSave: (notificationData: any) => void;
}

const SendNotificationModal: React.FC<SendNotificationModalProps> = ({ isOpen, onClose, onSave }) => {
  const [formData, setFormData] = useState({
    title: '',
    message: '',
    type: 'info' as 'info' | 'warning' | 'success' | 'error',
    recipients: [] as { id: string; name: string; role: string }[],
    sendToAll: false,
    sendEmail: true,
    sendSms: false,
    priority: 'normal' as 'low' | 'normal' | 'high'
  });

  const [searchTerm, setSearchTerm] = useState('');
  const [isSending, setIsSending] = useState(false);

  // Mock kullanıcı listesi
  const mockUsers = [
    { id: 'user-1', name: 'Fatma Özkan', email: 'egitim@pisaeducazione.com', role: 'Eğitim Danışmanı' },
    { id: 'user-2', name: 'Can Kaya', email: 'vize@pisaeducazione.com', role: 'Vize Danışmanı' },
    { id: 'user-3', name: 'Elif Yıldırım', email: 'pazarlama@pisaeducazione.com', role: 'Pazarlama Uzmanı' },
    { id: 'user-4', name: 'Ahmet Demir', email: 'satis@pisaeducazione.com', role: 'Satış Uzmanı' },
    { id: 'user-5', name: 'Hasan Çelik', email: 'muhasebe@pisaeducazione.com', role: 'Muhasebe Uzmanı' },
    { id: 'user-6', name: 'İstanbul Şubesi', email: 'istanbul@pisaeducazione.com', role: 'Şube Yöneticisi' },
    { id: 'student-1', name: 'Zeynep Kara', email: 'zeynep.kara@email.com', role: 'Öğrenci' },
    { id: 'student-2', name: 'Ahmet Yıldız', email: 'ahmet.yildiz@email.com', role: 'Öğrenci' }
  ];

  const filteredUsers = mockUsers.filter(user => 
    user.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    user.email.toLowerCase().includes(searchTerm.toLowerCase()) ||
    user.role.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
    const { name, value, type } = e.target;
    if (type === 'checkbox') {
      const checked = (e.target as HTMLInputElement).checked;
      setFormData(prev => ({ ...prev, [name]: checked }));
    } else {
      setFormData(prev => ({ ...prev, [name]: value }));
    }
  };

  const addRecipient = (user: { id: string; name: string; role: string }) => {
    if (!formData.recipients.some(r => r.id === user.id)) {
      setFormData(prev => ({
        ...prev,
        recipients: [...prev.recipients, user]
      }));
    }
    setSearchTerm('');
  };

  const removeRecipient = (userId: string) => {
    setFormData(prev => ({
      ...prev,
      recipients: prev.recipients.filter(r => r.id !== userId)
    }));
  };

  const getTypeIcon = (type: string) => {
    switch (type) {
      case 'info': return Info;
      case 'warning': return AlertTriangle;
      case 'success': return CheckCircle;
      case 'error': return AlertTriangle;
      default: return Info;
    }
  };

  const getTypeColor = (type: string) => {
    switch (type) {
      case 'info': return 'text-blue-600 bg-blue-100 dark:bg-blue-900/20 dark:text-blue-400';
      case 'warning': return 'text-yellow-600 bg-yellow-100 dark:bg-yellow-900/20 dark:text-yellow-400';
      case 'success': return 'text-green-600 bg-green-100 dark:bg-green-900/20 dark:text-green-400';
      case 'error': return 'text-red-600 bg-red-100 dark:bg-red-900/20 dark:text-red-400';
      default: return 'text-blue-600 bg-blue-100 dark:bg-blue-900/20 dark:text-blue-400';
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSending(true);
    
    // Simulate API call
    await new Promise(resolve => setTimeout(resolve, 1500));
    
    const notificationData = {
      ...formData,
      id: `notification-${Date.now()}`,
      createdAt: new Date(),
      recipients: formData.sendToAll ? mockUsers : formData.recipients
    };
    
    onSave(notificationData);
    
    setIsSending(false);
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 dark:bg-black/80 backdrop-blur-sm">
      <div className="bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-800 rounded-2xl w-full max-w-3xl mx-4 max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200 dark:border-gray-800">
          <div className="flex items-center space-x-3">
            <div className="w-10 h-10 bg-gradient-to-br from-yellow-500 to-orange-500 rounded-xl flex items-center justify-center">
              <Bell className="w-6 h-6 text-white" />
            </div>
            <h2 className="text-xl font-semibold text-gray-900 dark:text-white">Bildirim Gönder</h2>
          </div>
          <button
            onClick={onClose}
            className="p-2 hover:bg-gray-100 dark:hover:bg-gray-800 rounded-full transition-colors"
          >
            <X className="w-5 h-5 text-gray-500 dark:text-gray-400" />
          </button>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="p-6 space-y-6">
          {/* Bildirim Bilgileri */}
          <div>
            <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-4">Bildirim Bilgileri</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="md:col-span-2">
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Başlık <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  name="title"
                  value={formData.title}
                  onChange={handleChange}
                  required
                  className="w-full px-4 py-3 bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg text-gray-900 dark:text-white placeholder-gray-500 dark:placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  placeholder="Bildirim başlığı"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Bildirim Türü <span className="text-red-500">*</span>
                </label>
                <select
                  name="type"
                  value={formData.type}
                  onChange={handleChange}
                  required
                  className="w-full px-4 py-3 bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                >
                  <option value="info">Bilgi</option>
                  <option value="warning">Uyarı</option>
                  <option value="success">Başarılı</option>
                  <option value="error">Hata</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Öncelik
                </label>
                <select
                  name="priority"
                  value={formData.priority}
                  onChange={handleChange}
                  className="w-full px-4 py-3 bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                >
                  <option value="low">Düşük</option>
                  <option value="normal">Normal</option>
                  <option value="high">Yüksek</option>
                </select>
              </div>

              <div className="md:col-span-2">
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Mesaj <span className="text-red-500">*</span>
                </label>
                <textarea
                  name="message"
                  value={formData.message}
                  onChange={handleChange}
                  required
                  rows={4}
                  className="w-full px-4 py-3 bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg text-gray-900 dark:text-white placeholder-gray-500 dark:placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent resize-none"
                  placeholder="Bildirim mesajını yazın..."
                />
              </div>
            </div>
          </div>

          {/* Alıcılar */}
          <div>
            <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-4">Alıcılar</h3>
            
            {/* Herkese Gönder Seçeneği */}
            <div className="mb-4">
              <div className="flex items-center">
                <input
                  id="sendToAll"
                  type="checkbox"
                  name="sendToAll"
                  checked={formData.sendToAll}
                  onChange={handleChange}
                  className="w-4 h-4 text-blue-600 bg-gray-100 border-gray-300 rounded focus:ring-blue-500 dark:focus:ring-blue-600 dark:ring-offset-gray-800 focus:ring-2 dark:bg-gray-700 dark:border-gray-600"
                />
                <label htmlFor="sendToAll" className="ml-2 text-sm font-medium text-gray-900 dark:text-white">
                  Tüm kullanıcılara gönder
                </label>
              </div>
            </div>

            {!formData.sendToAll && (
              <>
                {/* Kullanıcı Arama */}
                <div className="mb-4">
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Alıcı Ekle
                  </label>
                  <div className="relative">
                    <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
                    <input
                      type="text"
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                      placeholder="İsim, email veya rol ile ara..."
                      className="w-full pl-10 pr-4 py-3 bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg text-gray-900 dark:text-white placeholder-gray-500 dark:placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    />
                  </div>
                  
                  {searchTerm && (
                    <div className="mt-2 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg shadow-lg max-h-60 overflow-y-auto">
                      {filteredUsers.length === 0 ? (
                        <div className="p-4 text-center text-gray-500 dark:text-gray-400">
                          Kullanıcı bulunamadı
                        </div>
                      ) : (
                        filteredUsers.map(user => (
                          <div 
                            key={user.id}
                            onClick={() => addRecipient({ id: user.id, name: user.name, role: user.role })}
                            className="p-3 hover:bg-gray-50 dark:hover:bg-gray-700 cursor-pointer flex items-center justify-between"
                          >
                            <div>
                              <p className="font-medium text-gray-900 dark:text-white">{user.name}</p>
                              <p className="text-sm text-gray-500 dark:text-gray-400">{user.email} • {user.role}</p>
                            </div>
                            <button 
                              type="button"
                              className="p-1 bg-blue-100 dark:bg-blue-900/30 text-blue-600 dark:text-blue-400 rounded-full hover:bg-blue-200 dark:hover:bg-blue-800/50"
                            >
                              <Plus className="w-4 h-4" />
                            </button>
                          </div>
                        ))
                      )}
                    </div>
                  )}
                </div>
                
                {/* Seçilen Alıcılar */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Seçilen Alıcılar ({formData.recipients.length})
                  </label>
                  {formData.recipients.length === 0 ? (
                    <div className="p-4 text-center text-gray-500 dark:text-gray-400 border border-dashed border-gray-300 dark:border-gray-700 rounded-lg">
                      Henüz alıcı eklenmedi
                    </div>
                  ) : (
                    <div className="space-y-2 max-h-40 overflow-y-auto">
                      {formData.recipients.map(recipient => (
                        <div 
                          key={recipient.id}
                          className="flex items-center justify-between p-3 bg-gray-50 dark:bg-gray-800 rounded-lg"
                        >
                          <div>
                            <span className="font-medium text-gray-900 dark:text-white">{recipient.name}</span>
                            <span className="text-sm text-gray-500 dark:text-gray-400 ml-2">({recipient.role})</span>
                          </div>
                          <button 
                            type="button"
                            onClick={() => removeRecipient(recipient.id)}
                            className="p-1 text-gray-500 hover:text-red-600 dark:text-gray-400 dark:hover:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20 rounded-full"
                          >
                            <X className="w-4 h-4" />
                          </button>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </>
            )}
          </div>

          {/* Gönderim Seçenekleri */}
          <div>
            <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-4">Gönderim Seçenekleri</h3>
            <div className="space-y-3">
              <div className="flex items-center">
                <input
                  id="sendEmail"
                  type="checkbox"
                  name="sendEmail"
                  checked={formData.sendEmail}
                  onChange={handleChange}
                  className="w-4 h-4 text-blue-600 bg-gray-100 border-gray-300 rounded focus:ring-blue-500 dark:focus:ring-blue-600 dark:ring-offset-gray-800 focus:ring-2 dark:bg-gray-700 dark:border-gray-600"
                />
                <label htmlFor="sendEmail" className="ml-2 text-sm font-medium text-gray-900 dark:text-white">
                  Email bildirimi gönder
                </label>
              </div>
              
              <div className="flex items-center">
                <input
                  id="sendSms"
                  type="checkbox"
                  name="sendSms"
                  checked={formData.sendSms}
                  onChange={handleChange}
                  className="w-4 h-4 text-blue-600 bg-gray-100 border-gray-300 rounded focus:ring-blue-500 dark:focus:ring-blue-600 dark:ring-offset-gray-800 focus:ring-2 dark:bg-gray-700 dark:border-gray-600"
                />
                <label htmlFor="sendSms" className="ml-2 text-sm font-medium text-gray-900 dark:text-white">
                  SMS bildirimi gönder
                </label>
              </div>
            </div>
          </div>

          {/* Önizleme */}
          {formData.title && formData.message && (
            <div>
              <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-4">Önizleme</h3>
              <div className="bg-gray-50 dark:bg-gray-800 rounded-xl p-4 border border-gray-200 dark:border-gray-700">
                <div className="flex items-start space-x-3">
                  <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${getTypeColor(formData.type)}`}>
                    {React.createElement(getTypeIcon(formData.type), { className: "w-5 h-5" })}
                  </div>
                  <div className="flex-1">
                    <h4 className="font-semibold text-gray-900 dark:text-white">{formData.title}</h4>
                    <p className="text-gray-600 dark:text-gray-400 mt-1">{formData.message}</p>
                    <div className="flex items-center space-x-4 mt-2 text-sm text-gray-500 dark:text-gray-500">
                      <span>Alıcı: {formData.sendToAll ? 'Tüm kullanıcılar' : `${formData.recipients.length} kişi`}</span>
                      <span>Öncelik: {formData.priority === 'high' ? 'Yüksek' : formData.priority === 'low' ? 'Düşük' : 'Normal'}</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Footer */}
          <div className="flex items-center justify-end space-x-3 pt-6 border-t border-gray-200 dark:border-gray-800">
            <button
              type="button"
              onClick={onClose}
              className="px-6 py-2 text-gray-600 dark:text-gray-400 hover:text-gray-800 dark:hover:text-gray-300 transition-colors"
            >
              İptal
            </button>
            <button
              type="submit"
              disabled={isSending || (!formData.sendToAll && formData.recipients.length === 0)}
              className={`flex items-center space-x-2 px-6 py-2 rounded-lg font-medium transition-all ${
                isSending || (!formData.sendToAll && formData.recipients.length === 0)
                  ? 'bg-gray-300 text-gray-500 cursor-not-allowed dark:bg-gray-700 dark:text-gray-500'
                  : 'bg-blue-600 hover:bg-blue-700 text-white hover:scale-105 hover:shadow-lg hover:shadow-blue-600/25'
              }`}
            >
              <Send className={`w-4 h-4 ${isSending ? 'animate-pulse' : ''}`} />
              <span>{isSending ? 'Gönderiliyor...' : 'Gönder'}</span>
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default SendNotificationModal;